import React, { useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/Ppulbatu/2Photobooth.css';

function ConfirmPpulbatu() {
  const location = useLocation();
  const navigate = useNavigate();
  const { images = [], selectedFrame } = location.state || {};
  const canvasRef = useRef(null);

  const handleConfirm = () => {
    navigate('/result/Ppulbatu', { state: { images, selectedFrame } });
  };

  const handleRetake = () => {
    navigate('/photobooth/Ppulbatu', { state: { existingImages: images, selectedFrame } });
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;

    const calculateCropDimensions = (imgWidth, imgHeight, targetWidth, targetHeight) => {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      let sourceX, sourceY, sourceWidth, sourceHeight;
      if (imgRatio > targetRatio) {
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2;
      } else {
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2;
        sourceY = 0;
      }
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    };

    if (isMobile) {
      const frameImg = new window.Image();
      frameImg.src = selectedFrame?.frameImage;

      frameImg.onload = () => {
        const maxWidth = Math.min(600, window.innerWidth - 40);
        const scaleFactor = maxWidth / frameImg.naturalWidth;
        const mobileWidth = Math.round(frameImg.naturalWidth * scaleFactor);
        const mobileHeight = Math.round(frameImg.naturalHeight * scaleFactor);

        canvas.width = mobileWidth;
        canvas.height = mobileHeight;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const photoWidth = mobileWidth * 0.91;
        const photoHeight = photoWidth / (4/3);
        const photoX = (mobileWidth - photoWidth) / 2;
        const topMargin = mobileHeight * 0.055;
        const spacing = mobileHeight * 0.002;

        const photoAreas = [
          { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
        ];

        let imagesLoaded = 0;
        const totalImages = images.length;

        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const area = photoAreas[i];

          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
                img.naturalWidth, img.naturalHeight, area.width, area.height
              );
              ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, area.x, area.y, area.width, area.height);
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
              }
            };
          } else {
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(area.x, area.y, area.width, area.height);
          }
        }

        if (totalImages === 0) {
          ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
        }
      };

      frameImg.onerror = () => {
        const mobileWidth = Math.min(400, window.innerWidth - 40);
        const mobileHeight = Math.round(mobileWidth * 4 / 3);
        canvas.width = mobileWidth;
        canvas.height = mobileHeight * 4;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const y = i * mobileHeight;
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => { ctx.drawImage(img, 0, y, mobileWidth, mobileHeight); };
          }
        }
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 3;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
      };

      return;
    }

    function drawPlaceholder(area) {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(area.x, area.y, area.width, area.height);
      ctx.fillStyle = '#999';
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', area.x + area.width/2, area.y + area.height/2);
    }

    const frameImg = new window.Image();
    frameImg.src = selectedFrame?.frameImage;

    const loadTimeout = setTimeout(() => { frameImg.onerror(); }, 10000);

    frameImg.onload = () => {
      clearTimeout(loadTimeout);
      canvas.width = frameImg.naturalWidth;
      canvas.height = frameImg.naturalHeight;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const frameWidth = frameImg.naturalWidth;
      const frameHeight = frameImg.naturalHeight;
      const photoWidth = frameWidth * 0.91;
      const photoHeight = photoWidth / (4/3);
      const photoX = (frameWidth - photoWidth) / 2;
      const topMargin = frameHeight * 0.055;
      const spacing = frameHeight * 0.002;

      const photoAreas = [
        { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
      ];

      let imagesLoaded = 0;
      const totalImages = images.length;
      const drawFrameOnTop = () => { ctx.drawImage(frameImg, 0, 0); };

      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const area = photoAreas[i];

        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth, img.naturalHeight, area.width, area.height
            );
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, area.x, area.y, area.width, area.height);
            imagesLoaded++;
            if (imagesLoaded === totalImages) { drawFrameOnTop(); }
          };
          img.onerror = () => {
            drawPlaceholder(area);
            imagesLoaded++;
            if (imagesLoaded === totalImages) { drawFrameOnTop(); }
          };
        } else {
          drawPlaceholder(area);
        }
      }

      if (totalImages === 0) { drawFrameOnTop(); }
    };

    frameImg.onerror = () => {
      clearTimeout(loadTimeout);
      const fallbackWidth = Math.min(600, window.innerWidth - 40);
      const fallbackHeight = Math.round(fallbackWidth * 4 / 3);
      canvas.width = fallbackWidth;
      canvas.height = fallbackHeight * 4;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const y = i * fallbackHeight;
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth, img.naturalHeight, fallbackWidth, fallbackHeight
            );
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, y, fallbackWidth, fallbackHeight);
          };
        } else {
          ctx.fillStyle = '#f0f0f0';
          ctx.fillRect(0, y, fallbackWidth, fallbackHeight);
        }
      }
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
    };
  }, [images, selectedFrame]);

  return (
    <div className="ResultScreen">
      <div className="content-container">
        <div className="photo-frame-container">
          {images && images.length > 0 ? (
            <canvas
              ref={canvasRef}
              className="photo-strip"
              style={{
                maxHeight: '650px',
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                border: 'none',
                boxShadow: 'none',
                display: 'block',
                margin: '0 auto',
                background: 'transparent'
              }}
            />
          ) : (
            <p className="no-photos">No photos taken. Please try again.</p>
          )}
        </div>

        <div className="right-panel">
          <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
            Do you want to retake or continue?
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
            <button
              onClick={handleRetake}
              className="download-button"
              style={{ backgroundColor: '#000000ff', padding: '15px 30px', fontSize: '18px' }}
            >
              {'>'} Retake 4 more photos
            </button>

            <button
              onClick={handleConfirm}
              className="download-button"
              style={{ backgroundColor: '#000000ff', padding: '15px 30px', fontSize: '18px' }}
            >
              {'>'} Create this photostrip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfirmPpulbatu;
